import React, { useEffect, useMemo, useState } from 'react';

import path from 'path';

import {
	ArrowDropUp as ArrowDropUpIcon,
	ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { Avatar, IconButton, Stack } from '@mui/material';
import { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { DataGrid } from '~components';
import { BaseReference } from '~interfaces';
import { buildAdyenLogoSvgUrl } from '~utils';

import TransactionSummary from '../../interfaces/transactionSummary';
import TransactionsService from '../../services/transactionsService';

const service = new TransactionsService();

interface OrganisationTransactionsSummaryDataGridProps {
	date: Date;
}

/**
 *
 * @returns
 * Notice if you change the props to a spread syntax, will create infinite rerender
 */
const OrganisationTransactionsSummaryDataGrid = ({
	date,
}: OrganisationTransactionsSummaryDataGridProps) => {
	const { t } = useTranslation();

	const { data, error, isLoading, isValidating } = useSWR([service.basePath, date], ([_, date]) => {
		if (isLoading) {
			service.abortCurrentRequest('Parameters change');
		}

		return service.getTransactionsSummaryByDate(date, {});
	});

	const [expanded, setExpanded] = useState<Record<number, boolean>>({});

	const dataFlat = data?.flatMap((result, i) => {
		const { paymentMethods, ...rest } = result;

		return [
			{ i: i, collapsable: true, ...rest },
			...(expanded[i] === true ?
				paymentMethods
					.map((el) => ({ i: `${i}.${el.paymentMethod ?? 'unknown'}`, ...el }))
					.sort((a, b) => (!b.paymentMethod || a.paymentMethod > b.paymentMethod ? 1 : -1))
			:	[]),
		];
	});

	const handleToggleExpanded = (id: string, value: boolean) => {
		setExpanded((prev) => ({ ...prev, [id]: value }));
	};

	const columns = useMemo<GridColDef<TransactionSummary>[]>(
		() => [
			{
				field: 'collapse',
				headerName: '',
				sortable: false,
				renderCell: (params) => {
					if (params.row.organisation === undefined) {
						return null;
					}

					const isExpanded = expanded[params.id] === true;
					return (
						<IconButton onClick={() => handleToggleExpanded(params.id, !isExpanded)}>
							{isExpanded ?
								<ArrowDropUpIcon />
							:	<ArrowDropDownIcon />}
						</IconButton>
					);
				},
			},
			{
				field: 'organisation',
				headerName: t('organisation'),
				valueFormatter: (value?: BaseReference) => value?.label ?? '',
				flex: 1,
			},
			// {
			// 	field: 'organisation',
			// 	headerName: t('organisation'),
			// 	valueFormatter: (value?: BaseReference) => value?.name ?? '',
			// 	flex: 1,
			// },
			{
				field: 'paymentMethod',
				headerName: t('ui.label.paymentMethod'),
				// valueFormatter: (_, row) => row.paymentMethod ?? (row.providerId == null ? '-' : 'all'),
				renderCell: (params) => (
					<Stack sx={{ height: 1, justifyContent: 'center' }}>
						{params.row.paymentMethod != null ?
							<Avatar
								src={buildAdyenLogoSvgUrl(params.row.paymentMethod)}
								variant='square'
								slotProps={{
									img: {
										style: {
											objectFit: 'contain',
										},
									},
								}}
							/>
						: params.row.organisation != null ?
							t('allResources', { resource: '' })
						:	'-'}
					</Stack>
				),
				flex: 1,
			},
			// {
			// 	field: 'total',
			// 	headerName: t('ui.label.item'),
			// 	flex: 1,
			// },
			{
				field: 'count',
				headerName: t('transactions'),
				flex: 1,
			},
		],
		[t, expanded]
	);

	return (
		<DataGrid
			getRowId={(row) => row.i}
			error={error}
			loading={isLoading || isValidating}
			columns={columns}
			rows={dataFlat ?? []}
			hideFooterPagination
			disableColumnSorting
			sx={{ flexGrow: 1, flexBasis: 200 }}
		/>
	);
};

export default OrganisationTransactionsSummaryDataGrid;
